import React, { useEffect } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";
import { useFormik } from "formik";
import { Box, Typography } from "@mui/material";

import messages from "../../../../assets/locale/messages";
import Breadcrumbs from "../../../../components/BreadCrumbs";
import { ROUTE_PATHS } from "../../../../utils/RoutesPaths";
import {
  getEvaluationRequest,
  updateEvaluationRequest,
} from "../../../../store/Evaluations/actions";
import { ONLY_POSITIVE_NUMBERS_WITH_ZERO } from "../../../../utils/Patterns";
import { evaluationFormStatus } from "../../../../utils/Constants";
import { UIButton, UISelect, UITextField } from "../../../../components/ui";
import "../Evaluations.scss";
import "./EditEvaluations.scss";

const statusOptions = [
  {
    id: 0,
    label: evaluationFormStatus.pending,
    name: evaluationFormStatus.pending,
  },
  {
    id: 1,
    label: evaluationFormStatus.submitted,
    name: evaluationFormStatus.submitted,
  },
];

const getInitialValues = (data) => {
  const initialStatus = statusOptions.find(
    (option) => option.label === data?.status
  );
  return {
    name: data?.name ?? "",
    number_of_applicants: data?.number_of_applicants ?? "",
    number_of_submitted_forms: data?.number_of_submitted_forms ?? "",
    status: initialStatus?.id ?? "",
  };
};

const EditEvaluations = () => {
  const dispatch = useDispatch();
  const { id } = useParams();

  const lang = useSelector((state) => state.locale.lang);
  const { evaluations, shared } = messages[lang];
  const list = useSelector((state) => state.evaluations.formData);

  const formik = useFormik({
    initialValues: getInitialValues(),
    validationSchema: Yup.object({
      name: Yup.string(),
      number_of_applicants: Yup.string()
        .matches(ONLY_POSITIVE_NUMBERS_WITH_ZERO, "onlyPositiveNumber")
        .nullable(),
      number_of_submitted_forms: Yup.string()
        .matches(ONLY_POSITIVE_NUMBERS_WITH_ZERO, "onlyPositiveNumber")
        .nullable(),
      status: Yup.mixed().nullable(),
    }),
    onSubmit: ({
      name,
      number_of_applicants,
      number_of_submitted_forms,
      status,
    }) => {
      dispatch(
        updateEvaluationRequest({
          id,
          evaluation: {
            name,
            number_of_submitted_forms: +number_of_submitted_forms,
            number_of_applicants: +number_of_applicants,
            status,
          },
        })
      );
    },
  });

  useEffect(() => {
    dispatch(getEvaluationRequest({ id }));
  }, [lang, id, dispatch]);

  useEffect(() => {
    if (list) {
      formik.setValues(getInitialValues(list));
    }
  }, [list]);

  const selectedStatus =
    statusOptions.find((option) => option.id === formik.values.status) || null;

  return (
    <div className="edit-evaluation-page">
      <div className="page-title-margin">
        <Breadcrumbs
          list={[
            {
              path: ROUTE_PATHS.events,
              label: evaluations.listEvaluations.title,
            },
            {
              path: "",
              label: shared.edit,
            },
          ]}
          BreadCrumbHasBackIcon={false}
        />
        <Box className="d-flex justify-content-between align-items-center">
          <Typography variant="h5">
            {evaluations.listForms.editEvaluations}
          </Typography>
        </Box>
      </div>

      <form className="edit-evaluation-page__form" onSubmit={formik.handleSubmit}>
        <div className="edit-evaluation-page__section">
          <div className="edit-evaluation-page__grid">
            <UITextField
              className="edit-evaluation-page__field"
              id="name"
              name="name"
              label={evaluations.listForms.name}
              placeholder={evaluations.listForms.name}
              value={formik.values.name}
              onValueChange={(value) => {
                formik.setFieldTouched("name", true);
                formik.setFieldValue("name", value);
              }}
              onBlur={() => formik.setFieldTouched("name", true)}
              error={Boolean(formik.touched.name && formik.errors.name)}
              helperText={formik.touched.name ? formik.errors.name : ""}
            />
            <UITextField
              className="edit-evaluation-page__field"
              id="number_of_applicants"
              name="number_of_applicants"
              type="number"
              label={evaluations.listForms.numberOfApplicants}
              placeholder={evaluations.listForms.numberOfApplicants}
              value={formik.values.number_of_applicants}
              onValueChange={(value) => {
                formik.setFieldTouched("number_of_applicants", true);
                formik.setFieldValue("number_of_applicants", value);
              }}
              onBlur={() => formik.setFieldTouched("number_of_applicants", true)}
              error={Boolean(
                formik.touched.number_of_applicants &&
                  formik.errors.number_of_applicants
              )}
              helperText={
                formik.touched.number_of_applicants
                  ? formik.errors.number_of_applicants
                  : ""
              }
            />
          </div>

          <div className="edit-evaluation-page__grid">
            <UITextField
              className="edit-evaluation-page__field"
              id="number_of_submitted_forms"
              name="number_of_submitted_forms"
              type="number"
              label={evaluations.listForms.numberOfSubmittedForm}
              placeholder={evaluations.listForms.numberOfSubmittedForm}
              value={formik.values.number_of_submitted_forms}
              onValueChange={(value) => {
                formik.setFieldTouched("number_of_submitted_forms", true);
                formik.setFieldValue("number_of_submitted_forms", value);
              }}
              onBlur={() =>
                formik.setFieldTouched("number_of_submitted_forms", true)
              }
              error={Boolean(
                formik.touched.number_of_submitted_forms &&
                  formik.errors.number_of_submitted_forms
              )}
              helperText={
                formik.touched.number_of_submitted_forms
                  ? formik.errors.number_of_submitted_forms
                  : ""
              }
            />
            <UISelect
              className="edit-evaluation-page__field"
              id="status"
              name="status"
              label={evaluations.listForms.status}
              placeholder={evaluations.listForms.status}
              options={statusOptions}
              value={selectedStatus}
              isOptionEqualToValue={(option, value) => option?.id === value?.id}
              onChange={(value) => {
                formik.setFieldTouched("status", true);
                formik.setFieldValue("status", value?.id ?? "");
              }}
              onBlur={() => formik.setFieldTouched("status", true)}
              error={Boolean(formik.touched.status && formik.errors.status)}
              helperText={formik.touched.status ? formik.errors.status : ""}
            />
          </div>
        </div>

        <div className="edit-evaluation-page__actions">
          <UIButton
            type="submit"
            appearance="gradient"
            fullWidth
            disabled={!formik.dirty || !formik.isValid}
          >
            {shared.confirm}
          </UIButton>
        </div>
      </form>
    </div>
  );
};

export default EditEvaluations;
